/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { ReactElement } from 'react';

export type GovernanceProtectionLevel = 'standard' | 'vault_only';

export interface GovernanceBadgeProps {
  readonly protectionLevel: GovernanceProtectionLevel;
}

function ShieldFilledIcon(): ReactElement {
  return (
    <svg
      className="ecs-governance-badge__icon-svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path d="M12 3l8 4v6c0 5-3.5 8.5-8 9.5C7.5 21.5 4 18 4 13V7l8-4z" />
    </svg>
  );
}

function LockIcon(): ReactElement {
  return (
    <svg
      className="ecs-governance-badge__icon-svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path strokeLinecap="round" d="M8 11V8a4 4 0 018 0v3" />
    </svg>
  );
}

const TOOLTIP_STANDARD =
  'This document is managed by the Ecsper Vault. All versions are saved and audited.';
const TOOLTIP_VAULT_ONLY =
  'This document is managed by the Ecsper Vault. Saving is restricted to the Ecsper Vault.';

/**
 * Compact badge indicating Vault governance. Upgrades to enforced vault-only
 * messaging when {@link protectionLevel} is `vault_only` (Stage 6 WOPI).
 */
export function GovernanceBadge({ protectionLevel }: GovernanceBadgeProps): ReactElement {
  const isVaultOnly = protectionLevel === 'vault_only';
  const label = isVaultOnly
    ? 'Vault-only document — saving restricted to the Ecsper Vault'
    : 'Vault-governed document';
  const tooltip = isVaultOnly ? TOOLTIP_VAULT_ONLY : TOOLTIP_STANDARD;

  return (
    <div
      className={`ecs-governance ecs-governance-badge${isVaultOnly ? ' ecs-governance-badge--vault-only' : ''}`}
      title={tooltip}
      role="status"
    >
      <span className="ecs-governance-badge__icon" aria-hidden="true">
        {isVaultOnly ? <ShieldFilledIcon /> : <LockIcon />}
      </span>
      <span className="ecs-governance-badge__text">{label}</span>
    </div>
  );
}
